import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type Ref } from 'react'

import type { ReactInputProps } from '../../types'
import { DISABLE_PASSWORD_MANAGERS_PROPS } from '../../util/passwordManagers'
import { type InputState, InternalInput } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'
import { useNumberInput } from './internal/useNumberInput'

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback called when the value in the input changes. The value provided will be a valid
   * number string, though it may contain more precision than can be represented by a JS
   * `Number` (e.g. by running the value through `Number.parseFloat`). We recommend using
   * something like [big.js](http://mikemcl.github.io/big.js/) to safely parse these values
   * if you need to operate on them and precision matters.
   *
   * In general you can just provide a callback here and things will work the way you expect.
   * However, it's worth noting that this callback will not be called for a change event if
   * the user has entered an invalid value (e.g. `'10.'` or a value smaller than `min`). If
   * the user enters an invalid number, `onChange` will instead be called one final time on
   * input blur with a fully-valid number `value`. As a concrete example of this, imagine the
   * user starts with an empty `<NumberInput max="5" value="">` and types `3`, `3`, `<tab>`.
   * This is what would happen:
   *
   * 1. On first `3` input, `onChange` is called with the value `'3'`.
   * 2. On second `3` input, `onChange` is not called because the value is invalid (33 > 5).
   * 3. On `<tab>`, the input is blurred, so the input value of `33` is normalized given the
   *    constraint of `max="5"` and `onChange` is called with a value of `'5'`.
   */
  onChange: ({ value }: { value: string }) => void
  /**
   * Specifies the amount by which the input value should be incremented or decremented
   * when the user presses the up or down arrow keys.
   *
   * The special value 'none' can be used to disable step functionality.
   * Note that '0' is not supported and will not disable step.
   */
  step?: string | 'none'
  /**
   * Specifies the minimum that the value will be constrained to.
   */
  min?: string
  /**
   * Specifies the maximum that the value will be constrained to.
   */
  max?: string
  /**
   * Specifies the number of decimal places that the value should be limited to. For example,
   * when buying fractional shares we may want to allow up to 6 decimal places (e.g. `0.835702`).
   *
   * Pass `0` if you only want integers to be accepted. If you pass `0` here, all relevant props
   * (e.g. `step`, `min`, `max`, `value`) should also be passed integer values.
   *
   * If omitted, this will default to either 2 or 4 depending on the current `value`: we allow
   * 4 decimal places in the range (-1, 1) but 2 outside that range. For example, 0.1234 and
   * 3.12 are valid by default but 3.1234 is not. This extra granularity can be useful for
   * things like maintaining specificity for fractional shares of expensive stocks.
   *
   * Migration note: <br />
   * In `web-app`'s various inputs (`PlainDecimalInput`, `MoneyInput`, etc.) this prop was
   * called `tickSize`. If you want the equivalent of `web-app`'s `IntegerInput`, pass `0` here.
   */
  maxDecimalPlaces?: number
  /**
   * Boolean indicating whether or not to add trailing '0' characters to the number string.
   */
  addTrailingZeros?: boolean
  /**
   * Boolean indicating whether or not to remove leading '0' characters from the number string.
   */
  removeLeadingZeros?: boolean
  /**
   * Provide `true` to  enable comma separators for large number inputs. Only formats on blur.
   * Formatting is not applied if the number has leading zeros. In the future aiming to format on change.
   */
  enableNumberSeparators?: boolean
  /**
   * Specifies which edge of the input the text should be aligned to. Note that `'start'`
   * means that the text will be on the left side of the input for a left-to-right language
   * like English.
   *
   * Migration note: <br />
   * When migrating from `LegacyInput`, use `'start'` instead of `'left'` and `'end'` instead
   * of `'right'`.
   */
  textAlign?: 'start' | 'end'
  /**
   * Callbacks that provide hooks to format the `value` string. `add` formats the string before
   * it is rendered into the `<input>` element, while `remove` formats it after it is changed
   * by the user, before it is normalized to adhere to the other options specified (e.g.
   * `maxDecimalPlaces`). These should be used only for visual formatting that does not affect
   * the value of the current `value`.
   *
   * For any string `x`, the following should hold: `x === remove(add(x))`.
   *
   * Migration note: <br />
   * In `web-app`'s various inputs (`PlainDecimalInput`, `MoneyInput`, etc.) `add` was called
   * `format` and `remove` was called `normalize`.
   */
  visualFormatting?: {
    add: (value: string) => string
    remove: (value: string) => string
  }
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do
   * this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

type DefaultInputProps = Omit<
  ReactInputProps,
  keyof Props | 'type' | 'disabled' | 'onChange' | 'css' | 'className' | 'style'
>

/**
 * An input element for collecting number values. It only accepts numeric characters and
 * related punctuation. Users can also use the up and down arrow keys to modify the value.
 * It can be configured to only accept values within a certain range or of certain
 * precision (e.g. integers only, or only two decimal places).
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 *
 * If the number you want to collect is a money value, please use `CurrencyInput` instead.
 */
export function NumberInput({
  addTrailingZeros = false,
  enableNumberSeparators = false,
  errorMessage,
  hideLabel = false,
  innerRef,
  label = '',
  max,
  maxDecimalPlaces,
  min,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  onKeyDown: onKeyDownProp,
  overrideRootStyles,
  removeLeadingZeros = false,
  state = 'default',
  step,
  textAlign = 'end',
  value: valueProp,
  visualFormatting,
  ...inputProps
}: Props & DefaultInputProps) {
  const numberInputProps = useNumberInput({
    addTrailingZeros,
    componentName: 'NumberInput',
    enableNumberSeparators,
    max,
    maxDecimalPlaces,
    min,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    onKeyDown: onKeyDownProp,
    removeLeadingZeros,
    step,
    textAlign,
    value: valueProp,
    visualFormatting,
  })

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          innerRef={innerRef}
          // Trigger numeric/decimal keyboard on mobile web.
          inputMode={maxDecimalPlaces === 0 ? 'numeric' : 'decimal'}
          state={state}
          {...numberInputProps}
          {...DISABLE_PASSWORD_MANAGERS_PROPS}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}
